import { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { motion, AnimatePresence } from "framer-motion";
import {
    TrophyOutlined,
    CrownOutlined,
    UserOutlined,
    TeamOutlined,
    ShopOutlined,
    AppstoreOutlined,
    GiftOutlined,
    CloseOutlined,
    ToolOutlined,
    FileTextOutlined,
} from "@ant-design/icons";

const API_BASE = "https://retailtextiles.timeserasoftware.in/api/ERP";

/**
 * The API returns mixed key casing ("billno", "NETAMT", "CustName"...).
 * JS property access is case-sensitive, so always read fields through pick().
 */
const pick = (row, field) => {
    if (!row || !field) return undefined;
    if (row[field] !== undefined) return row[field];
    const k = Object.keys(row).find(
        (key) => key.toLowerCase() === field.toLowerCase()
    );
    return k ? row[k] : undefined;
};

// "-" is how this ERP stores "empty", so treat it like blank.
const isBlank = (v) => {
    const s = v?.toString().trim();
    return !s || s === "-";
};

/**
 * ── CATEGORY CONFIG ──────────────────────────────────────────────────────
 * CREDIT_BILLMAST has ONE ROW PER BILL (no line items), so:
 *  - Customers / Dealers / Counter / Salesmen -> aggregate: group by the
 *    primaryField and sum NETAMT / totqty before ranking.
 *  - Invoices -> not aggregated: biggest single bills.
 *  - Products -> not possible from the bill master (no product names).
 *    Needs the bill line-items table; stays "ready: false" until then.
 *
 * skipBlank: true drops rows whose primaryField is "-" / empty so walk-ins
 * or unassigned rows don't collapse into one giant group at #1.
 */
const BASE = {
    tableName: "CREDIT_BILLMAST",
    amountField: "NETAMT", // NETAMT = after discount (totamt is before)
    piecesField: "totqty",
    ready: true,
};

const CATEGORY_CONFIG = [
    {
        ...BASE,
        key: "customers",
        label: "Customers",
        icon: <UserOutlined />,
        accent: { base: "#f9c74f", dark: "#b8860b", bg: "#fff8e6" },
        primaryField: "CustName",
        primaryFallback: "Walk-in Customer",
        metaField: "PhoneNo",
        aggregate: true,
        skipBlank: true,
    },
    {
        ...BASE,
        key: "invoices",
        label: "Invoices",
        icon: <FileTextOutlined />,
        accent: { base: "#ff9f43", dark: "#c76c00", bg: "#fff4e6" },
        primaryField: "CustName",
        primaryFallback: "Walk-in Customer",
        // metaField null on purpose: the row appends "Bill #<no> · <date>".
        metaField: null,
    },
    {
        key: "products",
        label: "Products",
        icon: <GiftOutlined />,
        accent: { base: "#ff6b81", dark: "#c81d4f", bg: "#fff0f3" },
        ready: false,
        note: "Products needs the bill line-items table",
    },
    {
        ...BASE,
        key: "dealers",
        label: "Suppliers",
        icon: <ShopOutlined />,
        accent: { base: "#4dd4ac", dark: "#1f9c74", bg: "#effaf5" },
        primaryField: "AGENTNAME", // assumption — change if another column holds the dealer
        primaryFallback: "Unassigned Dealer",
        metaField: null,
        aggregate: true,
        skipBlank: true,
    },
    {
        ...BASE,
        key: "counter",
        label: "Counter",
        icon: <AppstoreOutlined />,
        accent: { base: "#a374e8", dark: "#6b3fb8", bg: "#f5f0ff" },
        primaryField: "flrcode", // assumption — change if another column holds the counter
        primaryFallback: "Unassigned Counter",
        metaField: null,
        aggregate: true,
        skipBlank: true,
    },
    {
        ...BASE,
        key: "salesmen",
        label: "Salesmen",
        icon: <TeamOutlined />,
        accent: { base: "#4d96ff", dark: "#2d6abf", bg: "#f0f4ff" },
        primaryField: "SMCODE",
        primaryFallback: "Unassigned",
        metaField: "SalesmanMeta",
        aggregate: true,
        skipBlank: true,
        // Optional: resolve SMCODE -> name via an employee master. If this
        // table doesn't exist on the textile API the lookup fails quietly
        // and rows show "SM <code>". Remove this block to skip the call.
        join: {
            tableName: "BOYMAST",
            codeField: "CODE",
            nameField: "NAME",
        },
    },
];

const rankTier = (rank) => {
    if (rank === 1)
        return {
            label: "1st",
            ring: "#f9c74f",
            glow: "rgba(249,199,79,0.55)",
            chipBg: "linear-gradient(135deg,#fff3cf,#ffe49a)",
            text: "#8a6300",
            icon: <CrownOutlined />,
        };
    if (rank === 2)
        return {
            label: "2nd",
            ring: "#c0c0c0",
            glow: "rgba(192,192,192,0.5)",
            chipBg: "linear-gradient(135deg,#f3f3f3,#dcdcdc)",
            text: "#5f5f5f",
            icon: <TrophyOutlined />,
        };
    if (rank === 3)
        return {
            label: "3rd",
            ring: "#cd7f32",
            glow: "rgba(205,127,50,0.45)",
            chipBg: "linear-gradient(135deg,#fbe3cf,#eec19a)",
            text: "#7a4419",
            icon: <TrophyOutlined />,
        };
    return {
        label: `${rank}th`,
        ring: "#d9cdf5",
        glow: "rgba(163,116,232,0.3)",
        chipBg: "linear-gradient(135deg,#f5f0ff,#eae0ff)",
        text: "#6b3fb8",
        icon: null,
    };
};

const rowVariants = {
    hidden: { opacity: 0, x: -10 },
    show: (i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: Math.min(i * 0.04, 0.3), duration: 0.25 },
    }),
};

/** Colourful tile button that fills the card — no leftover blank space. */
const TileButton = ({ config, onClick }) => (
    <motion.button
        onClick={onClick}
        whileHover={{ y: -3, scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            height: "100%",
            minWidth: 0,
            border: `1px solid ${config.accent.base}55`,
            borderRadius: 14,
            cursor: "pointer",
            background: `linear-gradient(140deg, ${config.accent.bg} 0%, #ffffff 100%)`,
            padding: "14px 10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
        }}
    >
        <div
            style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: `${config.accent.base}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: config.accent.dark,
                fontSize: 17,
            }}
        >
            {config.icon}
        </div>
        <span
            style={{
                fontSize: 12,
                fontWeight: 800,
                color: config.accent.dark,
            }}
        >
            {config.label}
        </span>
    </motion.button>
);

/** Small looping pulse dot. */
const PulseDot = ({ color }) => (
    <span style={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
        <motion.span
            animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: color,
            }}
        />
        <span
            style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: color,
            }}
        />
    </span>
);

const EmptyState = ({ icon, color, title, subtitle }) => (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <div
            style={{
                width: 64,
                height: 64,
                margin: "0 auto 14px",
                borderRadius: "50%",
                background: `${color}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                color,
            }}
        >
            {icon}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2d1b69" }}>
            {title}
        </div>
        {subtitle && (
            <div style={{ fontSize: 12.5, color: "#9a8fc4", marginTop: 4 }}>
                {subtitle}
            </div>
        )}
    </div>
);

const TopTenRow = ({ record, index, config }) => {
    const rank = index + 1;
    const tier = rankTier(rank);
    // Textiles have no jewel type, so each row takes its category's colours.
    const accent = config.accent;
    const primaryValue =
        record[config.primaryField]?.toString().trim() || config.primaryFallback;
    const metaValue = config.metaField ? record[config.metaField] : null;

    // Aggregated rows represent many bills -> show a bill count;
    // single-bill rows show the bill number and date.
    const metaLine = [
        record.BillCount != null
            ? `${record.BillCount} bill${record.BillCount === 1 ? "" : "s"}`
            : record.BillNo != null
                ? `Bill #${record.BillNo}`
                : null,
        isBlank(metaValue) ? null : metaValue,
        record.BillDate ? dayjs(record.BillDate).format("DD MMM") : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <motion.div
            custom={index}
            variants={rowVariants}
            initial="hidden"
            animate="show"
            whileHover={{ y: -2 }}
            style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderRadius: 12,
                background: "#fff",
                border: `1px solid ${accent.base}33`,
                boxShadow:
                    rank <= 3
                        ? `0 3px 12px ${tier.glow}`
                        : "0 2px 8px rgba(44,27,105,0.05)",
            }}
        >
            {/* rank medal, with a soft looping glow for the top 3 */}
            <div style={{ position: "relative", width: 30, height: 30, flexShrink: 0 }}>
                {rank <= 3 && (
                    <motion.span
                        animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0.15, 0.55] }}
                        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                        style={{
                            position: "absolute",
                            inset: -4,
                            borderRadius: "50%",
                            background: tier.glow,
                        }}
                    />
                )}
                <div
                    style={{
                        position: "relative",
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: tier.chipBg,
                        border: `1.5px solid ${tier.ring}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: rank <= 3 ? 13 : 11,
                        fontWeight: 800,
                        color: tier.text,
                    }}
                >
                    {tier.icon || rank}
                </div>
            </div>

            {/* category avatar */}
            <div
                style={{
                    position: "relative",
                    width: 30,
                    height: 30,
                    borderRadius: 9,
                    flexShrink: 0,
                    background: `${accent.base}22`,
                    border: `1px solid ${accent.base}55`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: accent.dark,
                }}
            >
                {config.icon}
                <span style={{ position: "absolute", bottom: -2, right: -2 }}>
                    <PulseDot color={accent.dark} />
                </span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: "#2d1b69",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {primaryValue}
                </div>
                <div style={{ fontSize: 9.5, color: "#a89bc9", marginTop: 1 }}>
                    {metaLine}
                </div>
            </div>

            <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                    style={{
                        fontSize: 12.5,
                        fontWeight: 800,
                        color: "#1f9c74",
                        background: "#e8faf3",
                        borderRadius: 8,
                        padding: "2px 8px",
                    }}
                >
                    ₹
                    {Number(record.NetAmt || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                    })}
                </div>
                <div style={{ fontSize: 9, color: "#bbb", marginTop: 3 }}>
                    {Number(record.TotPieces || 0)} qty
                </div>
            </div>
        </motion.div>
    );
};

const TopTenStats = () => {
    const [activeKey, setActiveKey] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const tenantName = localStorage.getItem("tenantName");

    const activeConfig = CATEGORY_CONFIG.find((c) => c.key === activeKey);

    // Every dialog shows TODAY only — no date picker.
    const fetchTopTen = async (config) => {
        if (!config?.ready) return;
        setLoading(true);
        setErrorMsg("");
        try {
            const today = dayjs().format("YYYY-MM-DD");
            // Server-side filter: today's, non-cancelled bills only.
            const where = `CAST(BILLDATE AS DATE) = '${today}' AND CANCELL = 'NO'`;
            const url = `${API_BASE}/GetDataFromGivenTableNamewithwhere?tableName=${config.tableName}&where=${encodeURIComponent(where)}`;

            const response = await axios.get(url, { headers: { tenantName } });
            const allRows = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];

            // Safety net: if the backend ignores `where`, still show today only.
            const rows = allRows.filter(
                (r) =>
                    String(pick(r, "billdate") || "").slice(0, 10) === today &&
                    String(pick(r, "CANCELL") || "NO").toUpperCase() !== "YES"
            );

            const amt = (r) => Number(pick(r, config.amountField) || 0);
            const qty = (r) => Number(pick(r, config.piecesField) || 0);

            let top10;

            if (config.aggregate) {
                // One row per bill -> group by primaryField (customer /
                // dealer / counter / salesman) and sum amount + qty.
                const groups = new Map();
                rows.forEach((row) => {
                    const raw = pick(row, config.primaryField);
                    if (config.skipBlank && isBlank(raw)) return;
                    const key = raw?.toString().trim() || config.primaryFallback;
                    if (!groups.has(key)) {
                        groups.set(key, {
                            [config.primaryField]: key,
                            ...(config.metaField && {
                                [config.metaField]: pick(row, config.metaField),
                            }),
                            NetAmt: 0,
                            TotPieces: 0,
                            billNos: new Set(),
                        });
                    }
                    const g = groups.get(key);
                    g.NetAmt += amt(row);
                    g.TotPieces += qty(row);
                    const billNo = pick(row, "billno");
                    if (billNo != null) g.billNos.add(billNo);
                });

                top10 = Array.from(groups.values())
                    .map(({ billNos, ...g }) => ({ ...g, BillCount: billNos.size }))
                    .sort((a, b) => b.NetAmt - a.NetAmt)
                    .slice(0, 10);

                // Salesmen: BILL master only has SMCODE, so resolve the name
                // from an employee master for just the top 10 codes.
                if (config.join) {
                    try {
                        const joinRes = await axios.get(
                            `${API_BASE}/GetDataFromGivenTableName?tableName=${config.join.tableName}`,
                            { headers: { tenantName } }
                        );
                        const joinRows = Array.isArray(joinRes.data)
                            ? joinRes.data
                            : [];
                        const joinMap = new Map(
                            joinRows.map((r) => [
                                String(pick(r, config.join.codeField)).trim(),
                                r,
                            ])
                        );

                        top10 = top10.map((item) => {
                            const code = item[config.primaryField];
                            const emp = joinMap.get(String(code).trim());
                            return {
                                ...item,
                                [config.primaryField]:
                                    pick(emp, config.join.nameField) ||
                                    `SM ${code}`,
                                [config.metaField]: emp
                                    ? [pick(emp, "Designation"), pick(emp, "mobile")]
                                        .filter(Boolean)
                                        .join(" · ")
                                    : `Code ${code}`,
                            };
                        });
                    } catch (joinErr) {
                        console.error("Error resolving join table:", joinErr);
                        // keep the raw codes rather than failing the whole card
                    }
                }
            } else {
                // Invoices: biggest single bills.
                top10 = rows
                    .map((row) => ({
                        [config.primaryField]: pick(row, config.primaryField),
                        BillNo: pick(row, "billno"),
                        BillDate: pick(row, "billdate"),
                        NetAmt: amt(row),
                        TotPieces: qty(row),
                    }))
                    .sort((a, b) => b.NetAmt - a.NetAmt)
                    .slice(0, 10);
            }

            setData(top10);
        } catch (err) {
            console.error("Error fetching top 10:", err);
            setErrorMsg("Something went wrong. Please try again.");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const openCategory = (config) => {
        setData([]);
        setActiveKey(config.key);
    };

    useEffect(() => {
        if (activeConfig?.ready) {
            fetchTopTen(activeConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey]);

    return (
        <>
            <div
                style={{
                    background: "#fff",
                    borderRadius: 14,
                    padding: "12px 14px 14px",
                    boxShadow: "0 4px 20px rgba(44,27,105,0.09)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#2d1b69",
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            display: "inline-block",
                            width: 3,
                            height: 14,
                            borderRadius: 3,
                            background: "linear-gradient(180deg,#2d1b69,#b8a4e8)",
                        }}
                    />
                    <TrophyOutlined style={{ color: "#f9c74f", fontSize: 13 }} />
                    Top 10
                </div>

                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gridAutoRows: "1fr",
                        gap: 10,
                    }}
                >
                    {CATEGORY_CONFIG.map((config) => (
                        <TileButton
                            key={config.key}
                            config={config}
                            onClick={() => openCategory(config)}
                        />
                    ))}
                </div>
            </div>

            <style>{`
        .top10-modal .ant-modal-content { padding: 0; border-radius: 18px; overflow: hidden; }
        .top10-modal .ant-modal-body { padding: 0; }
      `}</style>

            <Modal
                open={!!activeConfig}
                onCancel={() => setActiveKey(null)}
                footer={null}
                closable={false}
                width={560}
                centered
                className="top10-modal"
                destroyOnClose
            >
                {activeConfig && (
                    <>
                        {/* gradient header, matching the accent of the chosen category */}
                        <div
                            style={{
                                position: "relative",
                                overflow: "hidden",
                                background: `linear-gradient(90deg, ${activeConfig.accent.dark}, ${activeConfig.accent.base})`,
                                padding: "20px 22px",
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <div
                                        style={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: 12,
                                            background: activeConfig.accent.bg,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: activeConfig.accent.dark,
                                            fontSize: 19,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {activeConfig.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>
                                            Top 10 {activeConfig.label}
                                        </div>
                                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                                            Today · {dayjs().format("DD MMM YYYY")}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setActiveKey(null)}
                                    style={{
                                        border: "none",
                                        background: "rgba(255,255,255,0.18)",
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        color: "#fff",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <CloseOutlined style={{ fontSize: 12 }} />
                                </button>
                            </div>
                        </div>

                        {/* body */}
                        <div style={{ padding: "18px 20px 22px", minHeight: 320 }}>
                            {!activeConfig.ready ? (
                                <EmptyState
                                    icon={<ToolOutlined />}
                                    color={activeConfig.accent.dark}
                                    title={activeConfig.note}
                                    subtitle="You'll see it here as soon as it's connected."
                                />
                            ) : loading ? (
                                <div style={{ textAlign: "center", padding: "56px 0" }}>
                                    <Spin size="large" />
                                </div>
                            ) : errorMsg ? (
                                <EmptyState
                                    icon={<TrophyOutlined />}
                                    color="#e05252"
                                    title={errorMsg}
                                />
                            ) : data.length === 0 ? (
                                <EmptyState
                                    icon={activeConfig.icon}
                                    color={activeConfig.accent.dark}
                                    title="No sales today yet"
                                    subtitle="New bills will show up here as they are made."
                                />
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        maxHeight: 440,
                                        overflowY: "auto",
                                        paddingRight: 4,
                                    }}
                                >
                                    <AnimatePresence>
                                        {data.map((record, i) => (
                                            <TopTenRow
                                                key={`${record[activeConfig.primaryField] || record.BillNo || i}-${i}`}
                                                record={record}
                                                index={i}
                                                config={activeConfig}
                                            />
                                        ))}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default TopTenStats;